import { Component, Input, OnInit, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { FormService } from '../service/form.service';
import { FormTemplate } from '../models/form.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AlertboxComponent } from '../../../helpers/alertbox/alertbox.component';
import { TransPipe } from '../../../helpers/trans/trans.pipe';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-form-details',
  standalone: true,
  imports: [CommonModule, DatePipe, TitleCasePipe, AlertboxComponent, TransPipe],
  templateUrl: './form-details.component.html',
  styleUrls: ['./form-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class FormDetailsComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() templateId!: string;

  template: FormTemplate | null = null;
  formLinks: any[] = [];
  loading = false;
  isPublishing = false;
  isArchiving = false;
  errorMessage = '';
  successMessage = '';
  
  // Success/Error feedback for app-alertbox
  showsuccessarchive = false;
  showerrorarchive = false;
  showconfirmarchive = false;

  constructor(private formService: FormService) {}

  ngOnInit(): void {
    this.loadTemplate();
    this.loadFormLinks();
  }

  loadTemplate(): void {
    this.loading = true;
    this.formService
      .getTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (templates) => {
          if (templates && templates.length > 0) {
            this.template = templates[0];
          }
          this.loading = false;
        },
        error: (err) => {
          this.errorMessage = 'Failed to load form details.';
          this.loading = false;
          console.error('Error loading template:', err);
        }
      });
  }

  loadFormLinks(): void {
    // This would load links from backend when that endpoint is available
    // For now, just initialize empty
    this.formLinks = [];
  }

  getTotalFields(): number {
    return this.template ? this.template.tabs.reduce((sum, tab) => sum + tab.fields.length, 0) : 0;
  }

  editForm(): void {
    console.log('Edit form:', this.templateId);
    // Navigate to builder component with templateId
  }

  publishForm(): void {
    if (!this.template) return;
    
    // Show confirmation dialog
    const nextVersion = this.template.version + 1;
    const confirmMessage = `Are you sure you want to publish this form?\n\n` +
      `This will publish version ${nextVersion}.\n` +
      `Published versions cannot be edited and become immutable.\n\n` +
      `Click OK to confirm, or Cancel to go back.`;
    
    if (!confirm(confirmMessage)) {
      return; // User cancelled
    }
    
    this.isPublishing = true;
    this.errorMessage = '';
    this.successMessage = '';
    
    this.formService
      .publishTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          // Try to parse version from response
          let publishedVersion = (this.template?.version || 1) + 1;
          const versionMatch = response.match(/version\s+(\d+)/i);
          if (versionMatch) {
            publishedVersion = parseInt(versionMatch[1], 10);
          }
          
          this.successMessage = `Form published successfully as version ${publishedVersion}!`;
          this.isPublishing = false;
          this.loadTemplate(); // Reload to get updated status and version
        },
        error: (err) => {
          const errorMessage = err.error?.message || err.message || 'Unknown error occurred';
          this.errorMessage = `Failed to publish form: ${errorMessage}`;
          this.isPublishing = false;
          console.error('Error publishing form:', err);
        }
      });
  }

  archiveForm(): void {
    if (!this.template) return;
    
    // Show confirmation dialog using alertbox
    this.showconfirmarchive = true;
  }

  onArchiveConfirmed(): void {
    this.showconfirmarchive = false;
    this.executeArchive();
  }

  onArchiveCancelled(): void {
    this.showconfirmarchive = false;
  }

  private executeArchive(): void {
    if (!this.template) return;
    
    this.isArchiving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.showerrorarchive = false;
    
    this.formService
      .archiveTemplate(this.organizationId, this.templateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          console.log('Template archived successfully, response:', response);
          this.showsuccessarchiveFunc();
          this.isArchiving = false;
          this.loadTemplate(); // Reload to get updated status
        },
        error: (err) => {
          console.error('Error archiving template:', err);
          this.errorMessage = err.error?.message || err.message || 'Failed to archive form template';
          this.showerrorarchiveFunc();
          this.isArchiving = false;
        }
      });
  }

  showsuccessarchiveFunc(): void {
    this.showsuccessarchive = true;
    setTimeout(() => {
      this.showsuccessarchive = false;
    }, 2000);
  }

  showerrorarchiveFunc(): void {
    this.showerrorarchive = true;
    setTimeout(() => {
      this.showerrorarchive = false;
    }, 2000);
  }

  duplicateForm(): void {
    if (!this.template) return;
    if (!confirm(`Duplicate "${this.template.name}"?`)) {
      return;
    }
    const duplicate = {
      name: `${this.template.name} (Copy)`,
      description: this.template.description,
      tabs: JSON.parse(JSON.stringify(this.template.tabs))
    };
    this.formService
      .createTemplate(this.organizationId, duplicate)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.successMessage = 'Form duplicated successfully!';
        },
        error: (err) => {
          this.errorMessage = 'Failed to duplicate form.';
          console.error('Error duplicating form:', err);
        }
      });
  }

  removeLink(linkId: string): void {
    // Remove form link when endpoint is available
    console.log('Remove link:', linkId);
  }

  goBack(): void {
    console.log('Go back to list');
    // Navigate back to list
  }
}
